/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { doNothing } from '../../language/module';
import { MODAL_SIZE } from './modalSize';
import Modal from './Modal';
import { COLUMN_SPACING } from '../../forms/styles';


const MODAL_STYLE = css`
  display: flex;
  min-width: ${MODAL_SIZE.SMALL};
  justify-content: center;
  flex-direction: column;
`;

export const ModalContent = styled('div')`
  width: 100%;
  box-sizing: border-box;
  padding: 0 28px; 
  margin-bottom: 28px;
  overflow-y: auto;
  overflow-x: hidden;
  ${COLUMN_SPACING};
`;


/**
 * ModalView: a component that renders a modal filling the whole window. This modal can be configured in a variety of
 * ways, see props for more details.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class ModalView extends React.PureComponent<{
  /**
   * The tooltip to show for the entire modal
   */
  accessibilityModalLabel?: string,
  /**
   * Whether or not the user is allowed to dismiss this modal (default: true)
   */
  dismissable?: boolean,
  /**
   * When the user goes to dismiss this modal, what does the parent want us to do?
   */
  onDismiss?: () => void,
  /**
   * Whether or not the user is allowed to dismiss this modal by clicking outside of the modal (default: false)
   */
  disableClickoffDismissable?: boolean,
  /**
   * Whether or not the user is allowed to dismiss this modal via keypress (default: false)
   */
  disableKeypressDismissable?: boolean,
  /**
   * Any styles to apply to the modal's root (not the background, but the root of what you would think of as the modal)
   */
  rootCss?: SerializedStyles,
  /**
   * The style to apply to the modal content wrapper (this is the parent of the children you give us)
   */
  contentCss?: SerializedStyles,
  /**
   * A preset size the modal should adhere to using the MODAL_SIZE enum. Default: MODAL_SIZE.DYNAMIC
   */
  size: MODAL_SIZE | string,
  children?: React.ReactNode,
}> {
  public static defaultProps = {
    dismissable: true,
    size:        MODAL_SIZE.DYNAMIC,
  };


  private onDismissModal = () => this.props.dismissable === true || this.props.dismissable === undefined
                                 ? (this.props.onDismiss || doNothing)()
                                 : doNothing();


  public render = () => (
    <Modal title={this.props.accessibilityModalLabel || ''}
           dismissable={this.props.dismissable}
           disableClickoffDismissal={this.props.disableClickoffDismissable}
           disableKeypressDismissal={this.props.disableKeypressDismissable}
           onDismiss={this.onDismissModal}
           rootCss={css`
             ${MODAL_STYLE};
             width: ${this.props.size};
             ${this.props.rootCss};
           `}>
      {/* Content */}
      {this.props.children}
    </Modal>
  );
}
